import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as Sentry from '@sentry/react';

import { handleApiError } from 'helper/error';
import { downloadBlob } from 'helper/downloadFile';
import { downloadBulletinBlob } from 'services/fileDownloadApi';
import { VideoProduct, videoProductToPath, videoProductToPermission } from 'helper/videos';

export const BULLETIN_DOWNLOAD_ERROR = 'Não foi possível baixar o boletim. Tente novamente.';

/**
 * Baixa o boletim como blob (via fileDownloadApi/axios), não mais como base64 em JSON pelo
 * RTK Query. Ver `downloadBulletinBlob` para o porquê.
 *
 * `fileName` vem do vídeo já carregado na página (`VideoData.file_name`, o mesmo campo que
 * habilita o botão) — antes o nome vinha junto do base64 na resposta, que agora não existe.
 */
export default function useBulletinDownload(
  id: number,
  product: VideoProduct,
  fileName?: string | null,
) {
  const navigate = useNavigate();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const onDownload = async () => {
    setDownloadLoading(true);
    setDownloadError('');

    try {
      const result = await downloadBulletinBlob({
        id,
        productPath: videoProductToPath[product],
        permissionType: videoProductToPermission[product],
      });

      if (result instanceof Blob) {
        downloadBlob(result, fileName || 'boletim.pdf');
      } else {
        handleApiError(navigate, result);
      }
    } catch (error) {
      // Boletins passam de 20 MB: a transferência é longa e morre por timeout de proxy ou
      // reset de conexão. É falha do download, não estado inválido da aplicação — avisa e
      // mantém o usuário na página, em vez de mandar para a tela genérica de erro.
      Sentry.captureException(error, {
        tags: { feature: 'videos-download-boletim' },
        extra: { id, product, fileName },
      });
      setDownloadError(BULLETIN_DOWNLOAD_ERROR);
    } finally {
      setDownloadLoading(false);
    }
  };

  return {
    onDownload,
    downloadLoading,
    downloadError,
  };
}
